import React, { useState } from "react";
import { Plus, Minus, Trash2, Star, Info, Loader2 } from "lucide-react";
import { VegDot } from "@/components/common";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { FoodInfoDialog } from "@/components/customer/FoodInfoDialog";

export function DishCard({ item, onAdd }) {
  const [info, setInfo] = useState(false);
  const [pending, setPending] = useState(false);
  const { cart, updateLine, removeLine } = useCart();

  const hasDisc = item.discount_pct > 0;
  const disc = Math.round(item.price * (1 - item.discount_pct / 100));
  const soldOut = item.in_stock === false;
  const customizable = !!(item.variants?.length || item.addons?.length || item.spice_levels?.length);

  const lines = (cart?.items || []).filter((l) => l.menu_item_id === item.id);
  const qtyInCart = lines.reduce((s, l) => s + l.qty, 0);
  const singleLine = lines.length === 1 ? lines[0] : null;
  const showStepper = !soldOut && !customizable && !!singleLine;

  // Guard prevents duplicate API calls from rapid taps.
  const guard = async (fn) => {
    if (pending) return;
    setPending(true);
    try { await fn(); } finally { setPending(false); }
  };
  const handleAdd = () => guard(() => onAdd(item));
  const inc = () => guard(() => updateLine(singleLine.line_id, 1));
  const dec = () => guard(() => (singleLine.qty === 1 ? removeLine(singleLine.line_id) : updateLine(singleLine.line_id, -1)));

  return (
    <div className="flex gap-3 py-4 border-b border-border" data-testid={`dish-${item.id}`}>
      <div className="flex-1 min-w-0">
        <VegDot veg={item.veg} />
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {item.bestseller && <span className="text-[10px] font-bold text-amber-600 flex items-center gap-0.5"><Star size={11} className="fill-amber-500 text-amber-500" /> Bestseller</span>}
          {item.recommended && !item.bestseller && <span className="text-[10px] font-bold text-coral">Recommended</span>}
        </div>
        <h4 className="font-bold text-ink text-[15px] mt-0.5 flex items-center gap-1.5">
          {item.name}
          <button onClick={() => setInfo(true)} className="text-ink-600 hover:text-coral tap" data-testid={`info-${item.id}`} aria-label="Food information"><Info size={14} /></button>
        </h4>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="font-semibold text-ink text-sm">{inr(hasDisc ? disc : item.price)}</span>
          {hasDisc && <span className="text-xs text-ink-600 line-through">{inr(item.price)}</span>}
          {item.rating && <span className="text-[11px] text-ink-600 flex items-center gap-0.5"><Star size={10} className="fill-gold text-gold" />{item.rating}</span>}
          {item.nutrition && <span className="text-[10px] font-bold text-veg">{item.nutrition.calories} cal</span>}
        </div>
        <p className="text-xs text-ink-600 mt-1 line-clamp-2">{item.description}</p>
      </div>

      <div className="relative shrink-0 w-28">
        {item.image
          ? <img src={item.image} alt={item.name} loading="lazy" className="w-28 h-24 object-cover rounded-xl" />
          : <div className="w-28 h-24 rounded-xl bg-surface border border-border" />}

        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[108px]">
          {soldOut ? (
            <div className="bg-white border border-border text-ink-600 font-bold text-xs py-2.5 rounded-lg text-center shadow-sm" data-testid={`soldout-${item.id}`}>Sold out</div>
          ) : showStepper ? (
            <div className="flex items-center justify-between bg-white border border-coral rounded-lg shadow-sm" data-testid={`stepper-${item.id}`}>
              <button onClick={dec} disabled={pending} className="w-9 h-9 flex items-center justify-center text-coral disabled:opacity-40 tap" aria-label="Decrease quantity" data-testid={`dish-minus-${item.id}`}>{singleLine.qty === 1 ? <Trash2 size={15} /> : <Minus size={16} />}</button>
              <span className="font-bold text-coral text-sm min-w-[18px] text-center" data-testid={`dish-qty-${item.id}`}>{singleLine.qty}</span>
              <button onClick={inc} disabled={pending} className="w-9 h-9 flex items-center justify-center text-coral disabled:opacity-40 tap" aria-label="Increase quantity" data-testid={`dish-plus-${item.id}`}>{pending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={16} />}</button>
            </div>
          ) : (
            <button onClick={handleAdd} disabled={pending} className="w-full bg-white border border-coral text-coral font-bold text-sm py-2.5 rounded-lg shadow-sm tap disabled:opacity-60 flex items-center justify-center gap-1" data-testid={`add-${item.id}`}>
              {pending ? <Loader2 size={15} className="animate-spin" /> : <>ADD{customizable ? <Plus size={13} /> : null}</>}
            </button>
          )}
          {customizable
            ? <span className="block text-[9px] text-center text-ink-600 mt-1" data-testid={`custom-note-${item.id}`}>{qtyInCart > 0 ? `${qtyInCart} in cart · customise` : "customisable"}</span>
            : null}
        </div>
      </div>
      <FoodInfoDialog item={item} open={info} onClose={() => setInfo(false)} />
    </div>
  );
}
